import { inject, Injectable } from '@angular/core'

import type { DocumentSnapshot, QueryFilterConstraint } from '@capacitor-firebase/firestore'
import { FirebaseFirestore, Timestamp } from '@capacitor-firebase/firestore'
import { combineLatest, distinctUntilChanged, Subject, takeUntil } from 'rxjs'

import { AssistantReplyPhase, AssistantToolCategory, ChatEvents } from '@ti/contracts'
import { AuthSession } from '@ti/core/auth/services'
import { ONE_MINUTE_MS } from '@ti/core/common/constants'
import { LOGIN_AS_SESSION } from '@ti/core/common/contracts'
import { injectDestroy$ } from '@ti/core/common/utils'
import { RealtimeClient } from '@ti/core/realtime'

import { unseenEvents } from './unseen-events'

export interface ChatEvent {
  type: ChatEvents
  driverId?: string
  threadId?: string
  messageId?: string
  messageType?: string
  createdBy?: string
  createdAt: Timestamp
  expiresAt: Timestamp
  message?: string
  /** `ReplyProgress` only: see `AssistantReplyProgress`. */
  seq?: number
  phase?: AssistantReplyPhase
  category?: AssistantToolCategory | null
  note?: string | null
  /** `ThreadUpdated` only: the thread was merged into this one, and leaves every list. */
  mergedIntoThreadId?: string
  /** `ThreadUpdated` only: this thread took in the merged one, and its messages changed. */
  mergedThreadId?: string
  /** `ThreadUpdated` only: staff linked this thread to the TSP, leaving a note among its messages. */
  linkedTspId?: number
}

/**
 * The events of AI replies. They come in bursts (text chunks, status changes, the end, the steps of
 * an action a reply proposed), and a TI user receives them for every TI thread, email triages
 * included.
 */
export const AI_REPLY_EVENTS: ChatEvents[] = [
  ChatEvents.ReplyGenerated,
  ChatEvents.ReplyProgress,
  ChatEvents.ReplyGenerateEnd,
  ChatEvents.ReplyGenerateError,
  ChatEvents.ThreadUpdated,
  ChatEvents.ActionUpdated,
]

/** Every other chat event: one per message or status change, never a burst. */
export const MESSAGE_EVENTS: ChatEvents[] = [
  ChatEvents.MessageSent,
  ChatEvents.MessageDeleted,
  ChatEvents.MessagesRead,
  ChatEvents.MessagesDelivered,
  ChatEvents.DriverOnline,
  ChatEvents.DriverOffline,
  ChatEvents.NotesUpdated,
]

@Injectable()
export class ChatUpdatesListener {
  public readonly onUpdates$ = new Subject<ChatEvent>()

  protected readonly authSession = inject(AuthSession)
  protected readonly loginAsSession = inject(LOGIN_AS_SESSION, { optional: true })
  protected readonly realtimeClient = inject(RealtimeClient)
  protected readonly destroy$ = injectDestroy$()

  protected readonly CHAT_COLLECTION_NAME = 'chat'

  protected readonly DRIVER_STATUSES_TTL = 5 * ONE_MINUTE_MS

  /**
   * How many of the newest events of each kind a snapshot holds. Writes that land together arrive
   * in one snapshot, so a window smaller than the burst loses its oldest events: with room for one,
   * a reply chunk or end event was swallowed by another thread's event. AI replies burst; messages
   * do not, and a smaller window replays less when an app resumes.
   */
  protected readonly AI_REPLY_EVENTS_WINDOW = 10
  protected readonly MESSAGE_EVENTS_WINDOW = 3

  public constructor() {
    Promise.resolve().then(() => this.init())
  }

  protected init() {
    this.initUpdatesListener()
    this.initDriverOnlineStatusLoading()

    this.destroy$.subscribe(() => {
      this.onUpdates$.complete()
    })
  }

  protected initUpdatesListener() {
    this.listenForEvents(AI_REPLY_EVENTS, this.AI_REPLY_EVENTS_WINDOW, 'AI reply')
    this.listenForEvents(MESSAGE_EVENTS, this.MESSAGE_EVENTS_WINDOW, 'message')
  }

  /**
   * Delivers each new event of `types` once, oldest first, from a listener over the newest
   * `window` of them. Only events since the listener started are read, and a new session replaces
   * the previous session's listener rather than running beside it.
   */
  protected listenForEvents(types: ChatEvents[], window: number, label: string) {
    let callbackId: string | undefined
    let seen = new Set<string>()

    const stop = async () => {
      if (!callbackId) return
      const id = callbackId
      callbackId = undefined
      await FirebaseFirestore.removeSnapshotListener({ callbackId: id })
    }

    combineLatest([this.authSession.state.currentUserAttributes$, this.realtimeClient.initialized$])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(([prev], [next]) => prev?.sub === next?.sub),
      )
      .subscribe(async ([sessionAttrs]) => {
        await stop()
        if (!sessionAttrs) return

        seen = new Set<string>()
        const filterConstraints: QueryFilterConstraint[] = [
          {
            type: 'where',
            fieldPath: 'createdAt',
            opStr: '>',
            value: Timestamp.fromDate(new Date(Date.now() - 1000)),
          },
          {
            type: 'where',
            fieldPath: 'type',
            opStr: 'in',
            value: types,
          },
        ]

        callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
          {
            reference: this.getChatEventsCollectionName(
              this.loginAsSession?.targetUserId ?? sessionAttrs.sub,
            ),
            compositeFilter: {
              type: 'and',
              queryConstraints: filterConstraints,
            },
            queryConstraints: [
              {
                type: 'orderBy',
                fieldPath: 'createdAt',
                directionStr: 'desc',
              },
              {
                type: 'limit',
                limit: window,
              },
            ],
          },
          (event, error) => {
            if (error) {
              console.error(`CHAT ${label} events listener error`, error)
              return
            }
            if (event) {
              const next = unseenEvents(event.snapshots as DocumentSnapshot<ChatEvent>[], seen)
              seen = next.seen
              next.events.forEach((update) => this.onUpdates$.next(update))
            }
          },
        )
      })

    this.destroy$.subscribe(() => {
      stop()
    })
  }

  // Load and replay the last relevant driver event if any
  protected initDriverOnlineStatusLoading() {
    let callbackId: string | undefined

    combineLatest([this.authSession.state.currentUserAttributes$, this.realtimeClient.initialized$])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(([prev], [next]) => prev?.sub === next?.sub),
      )
      .subscribe(async ([sessionAttrs]) => {
        if (!sessionAttrs) {
          if (callbackId) {
            await FirebaseFirestore.removeSnapshotListener({ callbackId })
            callbackId = undefined
          }
          return
        }

        const filterConstraints: QueryFilterConstraint[] = [
          {
            type: 'where',
            fieldPath: 'createdAt',
            opStr: '>',
            value: Timestamp.fromDate(new Date(Date.now() - this.DRIVER_STATUSES_TTL)),
          },
          {
            type: 'where',
            fieldPath: 'type',
            opStr: 'in',
            value: [ChatEvents.DriverOnline, ChatEvents.DriverOffline],
          },
        ]

        callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
          {
            reference: this.getChatEventsCollectionName(
              this.loginAsSession?.targetUserId ?? sessionAttrs.sub,
            ),
            compositeFilter: {
              type: 'and',
              queryConstraints: filterConstraints,
            },
            queryConstraints: [
              {
                type: 'orderBy',
                fieldPath: 'createdAt',
                directionStr: 'desc',
              },
              {
                type: 'limit',
                limit: 1,
              },
            ],
          },
          (event, error) => {
            if (error) {
              console.error('CHAT driver status listener error', error)
              return
            }

            if (event && event.snapshots.length >= 1) {
              this.onUpdates$.next(event.snapshots[0].data as ChatEvent)
            }
          },
        )

        this.destroy$.subscribe(() => {
          if (callbackId) {
            FirebaseFirestore.removeSnapshotListener({ callbackId })
          }
        })
      })
  }

  protected getChatEventsCollectionName(userId: string) {
    return `${this.CHAT_COLLECTION_NAME}/${userId}/events`
  }
}
